'use client';

import React from 'react';

import {SvgIcon} from '@mui/material';
import useAutocomplete from '@mui/material/useAutocomplete';
import {autocompleteClasses} from '@mui/material/Autocomplete';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiTypography from '@new-components/atoms/MuiTypography';
import {
  getSearchHistoryValues,
  RECENT_SEARCH_MAX_COUNT,
  syncWithStorage,
} from '@utils/new/searchHistory.utils';
import SVGIcons from '@components/SVGIcon';
import {Chip} from '@new-components/molecules/Chip';

export type IconMapType<T extends string> = Record<T, React.ReactNode>;
export type MuliSelectOption<T extends string> = {
  label: string;
  value: string;
  iconType: T;
};
export type MulitSelectSearchProps<T extends string> = {
  options: MuliSelectOption<T>[];
  placeholder: string;
  value: MuliSelectOption<T>[];
  onChange: (arg: MuliSelectOption<T>[]) => void;
  selectionLimit?: number;
  iconMap: IconMapType<T>;
  showSearchIcon?: boolean;
};

export function MultiSelectSearchBox<T extends string>(
  props: MulitSelectSearchProps<T>
) {
  const [highlightedOptions, setHighlightedOptions] = React.useState<
    React.ReactNode[]
  >([]);
  const [history, setHistory] = React.useState<string[]>(
    getSearchHistoryValues()
  ); //store history of selected options

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    value,
    focused,
    setAnchorEl,
    inputValue,
  } = useAutocomplete({
    id: 'mulit-select-search',
    multiple: true,
    defaultValue: props.value,
    value: props.value,
    options: props.options,
    getOptionLabel: (option) => option.label,
    onChange: (event, value, reason, details) => {
      props.onChange(value);
    },
  });

  React.useEffect(() => {
    //sync history on session
    syncWithStorage(
      props.options
        .sort((a, b) => {
          const aIndex = history.indexOf(a.value);
          const bIndex = history.indexOf(b.value);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        })
        .slice(0, history.length)
    );
  }, [history]);

  const renderTagsInsideSearchInput = (values: typeof props.options) => {
    if (values.length === 0 || focused) return null;

    const truncateTagLabel = `+${values.length - 1} more`;
    return (
      <>
        <MuiBox title={values[0].label}>
          <Chip
            {...getTagProps({index: 0})}
            label={values[0].label}
            maxWidth='100px'
          />
        </MuiBox>
        {values.length > 1 && (
          <Chip {...getTagProps({index: 1})} label={truncateTagLabel} />
        )}
      </>
    );
  };

  const renderTagsInsideDropdown = () => {
    return value.map((option, index) => {
      const {key, ...optionProps} = getTagProps({index});
      return <Chip key={key} {...optionProps} label={option.label} />;
    });
  };

  const selectedValues = value.map((option) => option.label);

  React.useEffect(() => {
    const searchQuery = inputValue.toLowerCase();
    if (searchQuery.length === 0) setHighlightedOptions([]);
    const highlightedOptions = (groupedOptions as typeof props.options).map(
      (group) => {
        const label = group.label;
        const start = label.toLowerCase().indexOf(searchQuery);
        const before = label.slice(0, start);
        const match = label.slice(start, start + searchQuery.length);
        const after = label.slice(start + searchQuery.length);
        return (
          <span>
            {before}
            <b>{match}</b>
            {after}
          </span>
        );
      }
    );
    setHighlightedOptions(highlightedOptions);
  }, [inputValue]);

  const applySelectionLimit = (fn: any, selectionLength: number) => {
    if (!props.selectionLimit || selectionLength < props.selectionLimit)
      return fn;
    return undefined;
  };

  const options = React.useMemo(
    () =>
      inputValue.length > 0
        ? (groupedOptions as typeof props.options)
        : (groupedOptions as typeof props.options).sort((a, b) => {
            const aIndex = history.indexOf(a.value);
            const bIndex = history.indexOf(b.value);
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
          }),
    [inputValue, history, groupedOptions]
  );

  React.useEffect(() => {
    setHistory((prev) =>
      [...value.reverse().map((option) => option.value), ...prev]
        .filter((v, i, a) => a.indexOf(v) === i)
        .slice(0, RECENT_SEARCH_MAX_COUNT)
    );
  }, [value]);

  return (
    <MuiBox {...getRootProps()} sx={{flex: '1 1 auto'}}>
      <MuiBox
        ref={setAnchorEl}
        sx={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          paddingInline: '12px',
          boxSizing: 'border-box',
          background: '#FFFFFF',
          borderRadius: '100px',

          ['&:hover']: {
            borderColor: '#40a9ff',
          },

          ['&.focused']: {
            borderColor: '#40a9ff',
            boxShadow: '0 0 0 2px rgb(24 144 255 / 0.2)',
          },
          ['& input']: {
            backgroundColor: '#fff',
            height: '30px',
            boxSizing: 'border-box',
            padding: '4px 6px',
            width: 0,
            minWidth: 30,
            flexGrow: 1,
            border: 0,
            margin: 0,
            outline: 0,
            textOverflow: 'ellipsis',

            color: '#2D2D2D',
            fontSize: '17px',
            fontWeight: 400,
            lineHeight: '23.8px',
            letterSpacing: '-0.4000000059604645px',
            textAlign: 'left',
            textUnderlinePosition: 'from-font',
            textDecorationSkipInk: 'none',
          },
        }}>
        {props.showSearchIcon && <SearchIcon />}
        {renderTagsInsideSearchInput(value)}
        <input
          {...getInputProps()}
          placeholder={value.length > 0 ? 'Add more..' : props.placeholder}
        />
      </MuiBox>
      <MuiBox
        sx={{
          width: '100%',
          margin: '26px 0 0',
          padding: 0,
          position: 'absolute',
          listStyle: 'none',
          overflow: 'auto',
          borderRadius: '24px',
          boxShadow: '0px 8px 16px 0px #00000014',
          zIndex: 1,
          backgroundColor: '#FFFFFF',
          display: focused ? 'block' : 'none',

          [`& li.${autocompleteClasses.focused}`]: {
            backgroundColor: '#F5F5F5',
            cursor: 'pointer',
          },
        }}>
        <MuiBox
          sx={{
            display: !focused || value.length === 0 ? 'none' : 'flex',
            flexWrap: 'wrap',
            padding: '12px 0px',
            margin: '0px 21px',
            gap: '12px',
            borderBottom: options.length > 0 ? '1px solid #82807E1A' : 'none',
          }}>
          {renderTagsInsideDropdown()}
        </MuiBox>
        <MuiBox
          {...getListboxProps()}
          component='ul'
          sx={{
            display: options.length > 0 ? 'flex' : 'none',
            flexDirection: 'column',
            padding: '0 21px',
            margin: '18px 0',
            maxHeight: '200px',
            overflow: 'auto',
          }}>
          {options.map((option, index) => {
            const {key, onClick, ...optionProps} = getOptionProps({
              option,
              index,
            });
            if (selectedValues.includes(option.label)) return null;
            return (
              <MuiBox
                id='search-box-option'
                component='li'
                key={key}
                onClick={applySelectionLimit(onClick, value.length)}
                {...optionProps}
                sx={{
                  fontSize: '15px',
                  fontWeight: 500,
                  lineHeight: '18.75px',
                  letterSpacing: '-0.4000000059604645px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  color: '#2D2D2D',
                  display: 'flex',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  cursor:
                    applySelectionLimit('pointer', value.length) ?? 'none',
                  opacity: applySelectionLimit(1, value.length) ?? 0.5,
                  gap: '12px',
                  alignItems: 'center',

                  ['&:hover']: {
                    background: '#82807E1A',
                  },
                }}>
                <MuiBox
                  component={'span'}
                  sx={{
                    display: 'flex',
                    svg: {
                      height: '24px',
                      width: '24px',
                    },
                  }}>
                  {inputValue.length === 0 && index < history.length ? (
                    <SVGIcons type='clock-svg' />
                  ) : (
                    props.iconMap[option.iconType]
                  )}
                </MuiBox>
                <MuiTypography>
                  {highlightedOptions.length !== 0
                    ? highlightedOptions[index]
                    : option.label}
                </MuiTypography>
              </MuiBox>
            );
          })}
        </MuiBox>
      </MuiBox>
    </MuiBox>
  );
}

const SearchIcon = () => (
  <SvgIcon sx={{height: '20px', width: '20px'}}>
    <svg
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M14.5835 14.583L18.3335 18.333'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M16.667 9.1665C16.667 5.02437 13.3091 1.6665 9.16699 1.6665C5.02486 1.6665 1.66699 5.02437 1.66699 9.1665C1.66699 13.3086 5.02486 16.6665 9.16699 16.6665C13.3091 16.6665 16.667 13.3086 16.667 9.1665Z'
        stroke='#2D2D2D'
        strokeWidth='1.25'
        strokeLinejoin='round'
      />
    </svg>
  </SvgIcon>
);
